import { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { savePostAuthRedirect } from "@/utils/oauth";

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: string;
}

export const ProtectedRoute = ({ children, fallback = "/login" }: ProtectedRouteProps) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      savePostAuthRedirect(location.pathname + location.search);
      navigate(fallback, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, fallback, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <img src="/icon.svg" alt="Joylo" className="h-24 w-24 animate-pulse" />
      </div>
    );
  }

  // If not authenticated, show branded splash while useEffect navigates to /login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <img src="/icon.svg" alt="Joylo" className="h-24 w-24 animate-pulse" />
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
};

// Higher-order component for protecting routes
export const withAuth = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => (
    <ProtectedRoute>
      <Component {...props} />
    </ProtectedRoute>
  );
};